import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useKid } from "@/context/KidContext";
import { toast } from "sonner";
import { Heart, Printer, PlayCircle, CheckCircle2, Tv, Target, Flag } from "lucide-react";
import { ContentCard } from "@/pages/HomeApp";
import Celebration from "@/components/Celebration";
import PremiumGate from "@/components/PremiumGate";

const KIND_BY_CATEGORY = {
  historias: "historia",
  atividades: "atividade",
  colorir: "atividade",
  jogos: "atividade",
  dinamicas: "atividade",
  devocionais: "atividade",
  oracoes: "oracao",
  versiculos: "versiculo",
  musicas: "atividade",
  planos: "atividade",
  valores: "atividade",
  imprimir: "atividade",
};

const DEFAULT_POINTS = { historia: 10, atividade: 10, versiculo: 5, oracao: 5, familia: 15 };

const YT_HOSTS = new Set([
  "youtube.com", "www.youtube.com", "m.youtube.com",
  "youtu.be", "www.youtu.be",
  "youtube-nocookie.com", "www.youtube-nocookie.com",
]);
const YT_ID_RE = /^[A-Za-z0-9_-]{11}$/;

/**
 * Detect and safely extract a YouTube video id from a user-provided URL.
 * Returns null when the URL is not a supported YouTube link.
 * We NEVER trust admin-provided HTML — only build the embed URL from a validated 11-char id.
 */
function parseYouTube(url) {
  if (!url || typeof url !== "string") return null;
  let u;
  try { u = new URL(url.trim()); } catch { return null; }
  if (!(u.protocol === "https:" || u.protocol === "http:")) return null;
  const host = u.hostname.toLowerCase();
  if (!YT_HOSTS.has(host)) return null;

  let id = null;
  if (host.endsWith("youtu.be")) {
    id = u.pathname.split("/").filter(Boolean)[0] || null;
  } else {
    const parts = u.pathname.split("/").filter(Boolean);
    if (parts[0] === "watch") id = u.searchParams.get("v");
    else if (parts[0] === "shorts" && parts[1]) id = parts[1];
    else if (parts[0] === "embed" && parts[1]) id = parts[1];
    else if (parts[0] === "live" && parts[1]) id = parts[1];
  }
  if (!id || !YT_ID_RE.test(id)) return null;
  const start = parseInt(u.searchParams.get("t") || u.searchParams.get("start") || "0", 10) || 0;
  return { id, start };
}

const DIRECT_VIDEO_RE = /\.(mp4|webm|ogv|ogg|m4v|mov)(\?.*)?$/i;

function computeExpected(content) {
  const kind = KIND_BY_CATEGORY[content.category] || "atividade";
  const points = Number.isFinite(content.points_awarded) ? content.points_awarded : (DEFAULT_POINTS[kind] || 5);
  const stars = Number.isFinite(content.stars_awarded) ? content.stars_awarded : Math.max(1, Math.floor(points / 5));
  return { kind, points, stars };
}

export default function ContentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { active, reloadKids } = useKid();
  const [data, setData] = useState(null);
  const [isFav, setIsFav] = useState(false);
  const [started, setStarted] = useState(false);
  const [celebration, setCelebration] = useState(null);
  const [nextEp, setNextEp] = useState(null);
  const [premiumBlock, setPremiumBlock] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportCategory, setReportCategory] = useState("conteudo_inadequado");
  const [reportDesc, setReportDesc] = useState("");
  const [reportSaving, setReportSaving] = useState(false);

  const submitReport = async () => {
    if (reportSaving) return;
    setReportSaving(true);
    try {
      await api.post("/reports/content", {
        content_id: id,
        category: reportCategory,
        description: (reportDesc || "").slice(0, 500),
      });
      toast.success("Reporte enviado. Obrigado por nos avisar 💛");
      setReportOpen(false);
      setReportDesc("");
      setReportCategory("conteudo_inadequado");
    } catch (e) {
      toast.error("Não foi possível enviar o reporte.");
    } finally {
      setReportSaving(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/contents/${id}`);
      setData(data);
      setPremiumBlock(null);
      setStarted(false);
      setNextEp(null);
      try {
        const favs = await api.get("/favorites");
        setIsFav(!!favs.data.favorites.find((f) => f.id === id));
      } catch {}
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403 && detail?.error === "premium_required") {
        setPremiumBlock({
          state: detail.entitlement_state || "none",
          checkout_url: detail.checkout_url || "",
        });
      } else if (e?.response?.status === 401 && detail?.premium_required) {
        setPremiumBlock({ state: "none", checkout_url: "" });
      } else {
        toast.error("Não foi possível carregar o conteúdo.");
      }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (premiumBlock) return (
    <PremiumGate
      entitlementState={premiumBlock.state}
      checkoutUrl={premiumBlock.checkout_url}
    />
  );

  if (!data) return <div className="p-10 text-center">Carregando…</div>;
  const c = data.content;
  const expected = computeExpected(c);
  const rawVideo = c.video_url || c.external_media_url || null;
  const yt = parseYouTube(rawVideo);
  const isDirectVideo = !yt && rawVideo && DIRECT_VIDEO_RE.test(rawVideo);
  const externalLink = !yt && !isDirectVideo && (c.external_media_url || null);
  const hasMedia = !!(c.audio_url || yt || isDirectVideo || externalLink);

  const toggleFav = async () => {
    if (isFav) {
      await api.delete(`/favorites/${id}`);
      setIsFav(false); toast.success("Removido dos favoritos");
    } else {
      await api.post("/favorites", { content_id: id });
      setIsFav(true); toast.success("Salvo nos favoritos 💛");
    }
  };

  const conclude = async () => {
    if (!active) return toast.error("Cadastre uma criança no seu perfil.");
    try {
      const { data: res } = await api.post("/progress", { child_id: active.id, content_id: id, kind: expected.kind });
      await reloadKids();
      // If this content is an episode of a series, pre-fetch the next episode
      if (c.series_key) {
        try {
          const { data: nx } = await api.get(`/series/${c.series_key}/next/${active.id}`, { params: { after: id } });
          setNextEp(nx.episode || null);
        } catch { setNextEp(null); }
      }
      setCelebration({
        points: res.earned_points,
        stars: res.earned_stars ?? Math.max(1, Math.floor(res.earned_points / 5)),
        achievements: res.new_achievements || [],
        kidName: active.name,
      });
    } catch (e) {
      toast.error("Não foi possível registrar.");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 pt-6 sm:pt-8 pb-16">
      <Link to={data.series ? `/series/${data.series.key}` : "/library"} data-testid="content-back-link" className="text-[#5DADE2] font-bold text-sm">
        {data.series ? `← ${data.series.title}` : "← Voltar para a Biblioteca"}
      </Link>

      {data.series && (
        <div className="mt-3 card-soft p-3 flex items-center gap-3 bg-white" data-testid="episode-breadcrumb">
          <Tv className="text-[#5DADE2] shrink-0" size={20} />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-bold text-[#243B53]/60">Série</div>
            <div className="font-extrabold text-[#243B53] truncate">
              {data.series.title}
              {c.season && <span className="text-[#243B53]/60 font-bold text-xs"> · Temporada {c.season}</span>}
              {c.episode_number && <span className="text-[#243B53]/60 font-bold text-xs"> · Episódio {c.episode_number}</span>}
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 grid lg:grid-cols-5 gap-6 lg:gap-8">
        <div className="lg:col-span-3">
          <div className="rounded-[2rem] overflow-hidden aspect-[16/10] bg-[#FFF9ED] relative">
            {c.cover_image ? (
              <img src={c.cover_image} alt={c.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-7xl">📖</div>
            )}
            <div className="absolute bottom-0 left-0 right-0 h-1.5 rainbow-strip opacity-90" />
          </div>

          {hasMedia && (
            <div className="mt-4 card-soft p-4" data-testid="content-media-block">
              <div className="text-xs font-bold text-[#D9A441] mb-2">🎵 Mídia</div>
              {c.audio_url && (
                <audio src={c.audio_url} controls className="w-full" data-testid="content-audio-player" />
              )}
              {yt && (
                <div
                  className="relative w-full overflow-hidden rounded-2xl bg-black"
                  style={{ paddingTop: "56.25%" }}
                  data-testid="content-youtube-embed"
                >
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src={`https://www.youtube-nocookie.com/embed/${yt.id}${yt.start ? `?start=${yt.start}` : ""}`}
                    title={c.title}
                    loading="lazy"
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
              )}
              {isDirectVideo && (
                <video
                  src={rawVideo}
                  controls
                  playsInline
                  className="w-full rounded-2xl bg-black"
                  data-testid="content-video-player"
                />
              )}
              {externalLink && (
                <a
                  href={externalLink}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#5DADE2] font-bold text-sm break-all inline-flex items-center gap-1"
                  data-testid="content-external-link"
                >
                  🔗 {externalLink}
                </a>
              )}
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-2">
            <span className="chip chip-sun capitalize">{c.category}</span>
            {c.theme && <span className="chip">{c.theme}</span>}
            {c.age_groups?.map((g) => <span key={g} className="chip chip-leaf">{g} anos</span>)}
            <span className="chip chip-gold">{c.duration_min} min</span>
            {c.difficulty && <span className="chip">{({muito_facil:"Muito fácil",facil:"Fácil",intermediario:"Intermediário"})[c.difficulty] || c.difficulty}</span>}
            {c.format && <span className="chip">{c.format}</span>}
            {c.collection && <span className="chip chip-gold">📚 {c.collection}</span>}
          </div>
          <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold text-[#243B53]">{c.title}</h1>
          <p className="mt-2 text-[#243B53]/80">{c.description}</p>

          {c.verse && (
            <section className="mt-5 card-soft p-5 bg-white border-l-4 border-[#D9A441]">
              <div className="text-xs font-bold text-[#D9A441]">✨ Versículo relacionado</div>
              <div className="mt-1 italic text-[#243B53]">{c.verse}</div>
            </section>
          )}

          {c.objective && (
            <section className="mt-5 card-soft p-5 bg-[#FFF9ED]">
              <div className="text-xs font-bold text-[#D9A441]">🎯 Objetivo</div>
              <div className="mt-1 text-[#243B53]">{c.objective}</div>
            </section>
          )}

          <section className="mt-5 card-soft p-5">
            <div className="text-xs font-bold text-[#5DADE2]">📖 Conteúdo</div>
            <div className="mt-2 whitespace-pre-line text-[#243B53]">{c.body}</div>
          </section>

          {(c.materials?.length ?? 0) > 0 && (
            <section className="mt-5 card-soft p-5">
              <div className="text-xs font-bold text-[#7BC67B]">🧰 Materiais necessários</div>
              <ul className="mt-2 list-disc pl-5 text-[#243B53]">
                {c.materials.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </section>
          )}

          {c.instructions && (
            <section className="mt-5 card-soft p-5">
              <div className="text-xs font-bold text-[#D9A441]">📝 Instruções</div>
              <div className="mt-2 whitespace-pre-line text-[#243B53]">{c.instructions}</div>
            </section>
          )}

          {data.related_activity && (
            <section className="mt-5 card-soft p-5 border-l-4 border-[#7BC67B]" data-testid="episode-mission">
              <div className="flex items-center gap-2">
                <Target size={18} className="text-[#7BC67B]" />
                <div className="text-xs font-bold text-[#7BC67B]">🎯 Missão deste episódio</div>
              </div>
              <div className="mt-1 font-extrabold text-[#243B53]">{data.related_activity.title}</div>
              <div className="text-sm text-[#243B53]/70">{data.related_activity.description}</div>
              <Link to={`/content/${data.related_activity.id}`} data-testid="episode-mission-btn" className="btn-secondary inline-block mt-3 text-sm">
                Ir para a missão
              </Link>
            </section>
          )}
        </div>

        <aside className="lg:col-span-2 space-y-3 lg:sticky lg:top-28 self-start">
          {!started ? (
            <button data-testid="content-start-btn" onClick={() => setStarted(true)} className="btn-primary w-full inline-flex items-center justify-center gap-2">
              <PlayCircle size={18} /> COMEÇAR
            </button>
          ) : (
            <button data-testid="content-complete-btn" onClick={conclude} className="btn-secondary w-full inline-flex items-center justify-center gap-2">
              <CheckCircle2 size={18} /> Concluir • +{expected.points} pontos
            </button>
          )}
          <button data-testid="content-fav-btn" onClick={toggleFav} className="btn-outline w-full inline-flex items-center justify-center gap-2">
            <Heart size={18} className={isFav ? "fill-[#D9A441] text-[#D9A441]" : ""} /> {isFav ? "Salvo" : "SALVAR"}
          </button>
          {c.printable_file && (
            <a data-testid="content-print-btn" href={c.printable_file.startsWith("http") ? c.printable_file : `${process.env.REACT_APP_BACKEND_URL}${c.printable_file}`} target="_blank" rel="noreferrer" className="btn-outline w-full inline-flex items-center justify-center gap-2">
              <Printer size={18} /> IMPRIMIR
            </a>
          )}
          <button
            data-testid="content-report-btn"
            onClick={() => setReportOpen(true)}
            className="w-full inline-flex items-center justify-center gap-2 text-sm text-[#243B53]/70 hover:text-[#D9A441] font-bold py-2"
          >
            <Flag size={14} /> Reportar problema
          </button>
          {active && (
            <div className="card-soft p-4 bg-[#FFF9ED]">
              <div className="text-xs font-bold text-[#243B53]/60">Registrando para</div>
              <div className="font-extrabold text-[#243B53]">{active.name}</div>
              <div className="mt-2 text-xs text-[#243B53]/70">Ganhará <b>+{expected.points}</b> pontos e <b>+{expected.stars}</b> estrelas ⭐</div>
            </div>
          )}
        </aside>
      </div>

      {data.related?.length > 0 && (
        <section className="mt-16">
          <h2 className="text-xl font-extrabold text-[#243B53]">Conteúdos relacionados</h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.related.map((r) => <ContentCard key={r.id} content={r} />)}
          </div>
        </section>
      )}

      {reportOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/40"
          data-testid="report-modal"
          onClick={() => setReportOpen(false)}
        >
          <div
            className="w-full max-w-md card-soft p-6 bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 text-[#D9A441]">
              <Flag size={18} />
              <h3 className="font-extrabold text-[#243B53]">Reportar problema</h3>
            </div>
            <p className="mt-1 text-xs text-[#243B53]/60">
              Seu reporte é revisado pela equipe. Não enviamos dados da criança.
            </p>
            <label className="block mt-4">
              <span className="text-sm font-bold text-[#243B53]">Categoria</span>
              <select
                data-testid="report-category-select"
                value={reportCategory}
                onChange={(e) => setReportCategory(e.target.value)}
                className="mt-1 w-full rounded-2xl border border-[#243B53]/15 bg-white px-4 py-2.5"
              >
                <option value="conteudo_inadequado">Conteúdo inadequado</option>
                <option value="erro_material">Erro no material</option>
                <option value="problema_tecnico">Problema técnico</option>
                <option value="outro">Outro problema</option>
              </select>
            </label>
            <label className="block mt-3">
              <span className="text-sm font-bold text-[#243B53]">Descrição (opcional)</span>
              <textarea
                data-testid="report-description-input"
                value={reportDesc}
                onChange={(e) => setReportDesc(e.target.value.slice(0, 500))}
                rows={4}
                maxLength={500}
                placeholder="Conte brevemente o que aconteceu"
                className="mt-1 w-full rounded-2xl border border-[#243B53]/15 bg-white px-4 py-2.5 text-sm"
              />
              <span className="text-xs text-[#243B53]/50">{reportDesc.length}/500</span>
            </label>
            <div className="mt-5 flex gap-3">
              <button
                data-testid="report-cancel-btn"
                onClick={() => setReportOpen(false)}
                className="btn-outline flex-1"
              >
                Cancelar
              </button>
              <button
                data-testid="report-submit-btn"
                onClick={submitReport}
                disabled={reportSaving}
                className="btn-primary flex-1 disabled:opacity-50"
              >
                {reportSaving ? "Enviando…" : "Enviar reporte"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Celebration
        open={!!celebration}
        onClose={() => {
          setCelebration(null);
          if (nextEp) {
            navigate(`/content/${nextEp.id}`);
            setNextEp(null);
          }
        }}
        kidName={celebration?.kidName}
        points={celebration?.points || 0}
        stars={celebration?.stars || 0}
        achievements={celebration?.achievements || []}
        nextEpisode={nextEp}
      />
    </div>
  );
}
